import React, { useCallback, useEffect, useRef, useState } from 'react';

import { Box, Center, HStack, Stack } from '@chakra-ui/react';
import { isEmpty, map } from 'lodash';
import { useDispatch, useSelector } from 'react-redux';

import { BorderedBox } from 'components/DataDisplay/Box/BorderedBox';
import { FilterLabelWrapper } from 'components/DataDisplay/Box/BorderedBox/FilterLabelWrapper';
import { CustomSpinner } from 'components/DataDisplay/Spinner/CustomSpinner';
import { WithSpinner } from 'components/DataDisplay/Spinner/WithSpinner';
import { Select } from 'components/DataEntry';
import { NoDataAvailable } from 'components/index';
import { usePageContentContext } from 'components/Layout';
import { CloudInsightItem } from 'containers/App/CloudInsight/CloudInsightItem';
import { selectApp } from 'containers/App/selectors';
import { actions } from 'containers/App/slice';
import { runtimeInsightsCategoryOptions } from 'containers/App/utils';

import { useAccessBoundary } from '../hooks/useAccessBoundary';

interface CloudInsightListProps {}

export const CloudInsightList = (props: CloudInsightListProps) => {
  const {
    cloudInsights,
    cloudInsightPage,
    runtimeInsightsCategories,
  } = useSelector(selectApp);
  const dispatch = useDispatch();
  const isLoading = useRef(false);
  const { contentHeight } = usePageContentContext();
  const { accountId, environmentId } = useAccessBoundary();
  const [isPageReloaded, setIsPageReloaded] = useState(true);

  useEffect(() => {
    dispatch(
      actions.getCloudInsights({
        q: {
          accountId: !!accountId ? [accountId] : [],
          environmentId,
          page: cloudInsightPage,
          pageSize: 20,
          categories: map(runtimeInsightsCategories, o => o.value),
        },
        onSuccess: () => {
          isLoading.current = false;
          setIsPageReloaded(false);
        },
        onError: () => {
          isLoading.current = false;
        },
      }),
    );
  }, [
    cloudInsightPage,
    dispatch,
    runtimeInsightsCategories,
    accountId,
    environmentId,
  ]);

  const handleScroll = useCallback(
    e => {
      const pullNext =
        e.target.scrollTop >
        e.target.scrollHeight - e.target.clientHeight - 100;
      if (isLoading.current) {
        return;
      }

      if (pullNext && !cloudInsights.isLoading) {
        isLoading.current = true;
        dispatch(actions.updateCloudInsightPage(cloudInsightPage + 1));
      }
    },
    [cloudInsightPage, cloudInsights.isLoading, dispatch],
  );

  return (
    <Stack w="full" h={contentHeight + 'px'} spacing={0} overflow="hidden">
      <Box pr={4} flexShrink={0} pb={5}>
        <BorderedBox>
          <HStack>
            <FilterLabelWrapper>FILTER BY</FilterLabelWrapper>
            <Box w="40%" zIndex={900} pr={10}>
              <Select
                options={runtimeInsightsCategoryOptions}
                value={runtimeInsightsCategories}
                onChange={s => {
                  dispatch(actions.updateRuntimeInsightsCategories(s));
                }}
                showTotalSelected
                showIconInValueContainer
                isMulti
                isDisabled={cloudInsights.isLoading}
              />
            </Box>
          </HStack>
        </BorderedBox>
      </Box>
      <Box flex={1} minH={0} position="relative">
        <WithSpinner
          loadStatus={{
            loading:
              (isEmpty(cloudInsights.data) && cloudInsights.isLoading) ||
              isPageReloaded,
          }}
        >
          {isEmpty(cloudInsights.data) ? (
            <Center h="full" flex={1}>
              <NoDataAvailable text="No insights available" />
            </Center>
          ) : (
            <Stack
              spacing={0}
              pb={0}
              onScroll={handleScroll}
              h="full"
              overflow="auto"
            >
              {map(cloudInsights.data, (insight, index) => {
                return (
                  <CloudInsightItem
                    item={insight}
                    key={
                      insight.event_time +
                      '/' +
                      insight.insight_id +
                      '/' +
                      insight.account_id
                    }
                    isLast={index === cloudInsights.data.length - 1}
                  />
                );
              })}
              {!isEmpty(cloudInsights.data) && cloudInsights.isLoading && (
                <Center
                  pos={'absolute'}
                  left={0}
                  bottom={0}
                  w={'full'}
                  h="100px"
                >
                  <CustomSpinner size="lg" />
                </Center>
              )}
            </Stack>
          )}
        </WithSpinner>
      </Box>
    </Stack>
  );
};
